import { motion } from "framer-motion";

const TechLoader = () => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
      <div className="relative flex flex-col items-center gap-6">
        {/* AI Brain/Circuit icon animation */}
        <div className="relative w-24 h-24">
          {/* Outer rotating ring */}
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-primary/30"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />
          
          {/* Middle pulsing ring */}
          <motion.div
            className="absolute inset-2 rounded-full border-2 border-primary/50"
            animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {/* Inner rotating ring (opposite direction) */}
          <motion.div
            className="absolute inset-4 rounded-full border-2 border-primary"
            animate={{ rotate: -360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          />
          
          {/* Center AI core */}
          <motion.div
            className="absolute inset-6 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center"
            animate={{ 
              boxShadow: [
                "0 0 20px hsl(199 89% 60% / 0.3)",
                "0 0 40px hsl(199 89% 60% / 0.6)",
                "0 0 20px hsl(199 89% 60% / 0.3)"
              ]
            }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            {/* Neural network dots */}
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-primary-foreground">
              <motion.circle
                cx="12"
                cy="12"
                r="2"
                fill="currentColor"
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              />
              <motion.circle
                cx="6"
                cy="8"
                r="1.5"
                fill="currentColor"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: 0.1 }}
              />
              <motion.circle
                cx="18"
                cy="8"
                r="1.5"
                fill="currentColor"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
              />
              <motion.circle
                cx="6"
                cy="16"
                r="1.5"
                fill="currentColor"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: 0.3 }}
              />
              <motion.circle
                cx="18"
                cy="16"
                r="1.5"
                fill="currentColor"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
              />
              {/* Connection lines */}
              <motion.line
                x1="12" y1="12" x2="6" y2="8"
                stroke="currentColor"
                strokeWidth="0.5"
                animate={{ opacity: [0.3, 0.8, 0.3] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              />
              <motion.line
                x1="12" y1="12" x2="18" y2="8"
                stroke="currentColor"
                strokeWidth="0.5"
                animate={{ opacity: [0.3, 0.8, 0.3] }}
                transition={{ duration: 0.8, repeat: Infinity, delay: 0.1 }}
              />
              <motion.line
                x1="12" y1="12" x2="6" y2="16"
                stroke="currentColor"
                strokeWidth="0.5"
                animate={{ opacity: [0.3, 0.8, 0.3] }}
                transition={{ duration: 0.8, repeat: Infinity, delay: 0.2 }}
              />
              <motion.line
                x1="12" y1="12" x2="18" y2="16"
                stroke="currentColor"
                strokeWidth="0.5"
                animate={{ opacity: [0.3, 0.8, 0.3] }}
                transition={{ duration: 0.8, repeat: Infinity, delay: 0.3 }}
              />
            </svg>
          </motion.div>
          
          {/* Orbiting particles */}
          {[0, 1, 2, 3].map((i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-primary"
              style={{ top: "50%", left: "50%" }}
              animate={{
                x: [0, Math.cos((i * Math.PI) / 2) * 40, 0],
                y: [0, Math.sin((i * Math.PI) / 2) * 40, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
        
        {/* Loading text */}
        <motion.div
          className="flex items-center gap-1 font-display text-lg text-primary"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <span>Initializing</span>
          <motion.span
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
          >
            .
          </motion.span>
          <motion.span
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
          >
            .
          </motion.span>
          <motion.span
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
          >
            .
          </motion.span>
        </motion.div>
      </div>
    </div>
  );
};

export default TechLoader;
